// src/modules/cv/NeuralCVLab.jsx
// Neural CV Lab — Interactive convolution demo, activations, pooling, CNN architectures, receptive field

import React, { useState, useMemo, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { generateSynth, imgToImageData, imageDataToURL, convolve, KERNELS } from '../../utils/cvUtils.js';
import { Panel, InfoBox } from '../../components/ui.jsx';

const TABS = ['convolution', 'activations', 'pooling', 'architectures', 'receptive', 'concepts'];
const Btn = ({onClick,children,color='#f472b6',disabled,style})=>(<button onClick={onClick} disabled={disabled} style={{padding:'8px 16px',background:color+'18',border:`1px solid ${color}`,color,fontSize:'10px',cursor:disabled?'not-allowed':'pointer',borderRadius:'3px',fontFamily:'JetBrains Mono',fontWeight:700,opacity:disabled?.4:1,...style}}>{children}</button>);

const PRESET_KERNELS = {
  identity:  [[0,0,0],[0,1,0],[0,0,0]],
  sharpen:   [[0,-1,0],[-1,5,-1],[0,-1,0]],
  blur:      [[1,1,1],[1,1,1],[1,1,1]],
  sobelX:    [[-1,0,1],[-2,0,2],[-1,0,1]],
  laplacian: [[0,1,0],[1,-4,1],[0,1,0]],
};

function computeConv(input, kernel) {
  const sz = input.length, ksz = kernel.length, half = Math.floor(ksz/2);
  const result = [];
  for(let y=1;y<sz-1;y++){
    const row=[];
    for(let x=1;x<sz-1;x++){
      let sum=0;
      for(let ky=0;ky<ksz;ky++) for(let kx=0;kx<ksz;kx++) sum+=(input[y+ky-half]?.[x+kx-half]??0)*kernel[ky][kx];
      row.push(+sum.toFixed(1));
    }
    result.push(row);
  }
  return result;
}

function sigmoid(x){return 1/(1+Math.exp(-x));}
function relu(x){return Math.max(0,x);}
function tanh2(x){return Math.tanh(x);}
function leakyRelu(x,a=0.1){return x>0?x:a*x;}
function gelu(x){return x*0.5*(1+Math.tanh(Math.sqrt(2/Math.PI)*(x+0.044715*x**3)));}

const ACTIVATIONS = [
  {id:'sigmoid', label:'Sigmoid',     color:'var(--cyan)',   fn:sigmoid,     formula:'σ(x) = 1/(1+e⁻ˣ)',  range:'(0,1)',  use:'Output layer for binary classification. Saturates → vanishing gradients.'},
  {id:'relu',    label:'ReLU',        color:'var(--green)',  fn:relu,        formula:'max(0, x)',          range:'[0,∞)', use:'Most common hidden layer activation. No saturation for x>0. Fast. Can die (gradient=0 for x<0).'},
  {id:'tanh',    label:'Tanh',        color:'var(--gold)',   fn:tanh2,       formula:'(eˣ−e⁻ˣ)/(eˣ+e⁻ˣ)',range:'(-1,1)', use:'Like sigmoid but zero-centered. Better gradients. Still saturates at extremes.'},
  {id:'leaky',   label:'Leaky ReLU',  color:'var(--orange)', fn:x=>leakyRelu(x), formula:'max(0.1x, x)', range:'(-∞,∞)', use:'Fixes dying ReLU by allowing small gradient for x<0 (α=0.1).'},
  {id:'gelu',    label:'GELU',        color:'#f472b6',       fn:gelu,        formula:'x·Φ(x)',            range:'(-∞,∞)', use:'Used in BERT, GPT, ViT. Smooth version of ReLU. Better performance on NLP/vision transformers.'},
];

const CNN_ARCHS = [
  {name:'VGG-16',color:'var(--cyan)',params:'138M',desc:'16 layers, all 3×3 convs. Simple & deep. Very large. Memory-heavy. Good baseline.'},
  {name:'ResNet-50',color:'var(--green)',params:'25M',desc:'Residual connections skip layers, enabling very deep nets (50–152 layers). Solves vanishing gradient. Won ImageNet 2015.'},
  {name:'Inception',color:'var(--gold)',params:'27M',desc:'Inception modules run 1×1, 3×3, 5×5 convs in parallel, concatenate. Multi-scale feature extraction.'},
  {name:'MobileNet',color:'var(--orange)',params:'4M',desc:'Depthwise separable convolutions: 8–9× fewer FLOPs than standard conv. Designed for mobile/edge devices.'},
  {name:'EfficientNet',color:'var(--violet)',params:'5-66M',desc:'Compound scaling of depth/width/resolution. State-of-art efficiency. B0–B7 variants.'},
  {name:'ViT',color:'#f472b6',params:'86M',desc:'Vision Transformer: split image into 16×16 patches, treat each as a token, apply self-attention. No convolutions. Needs large data.'},
];

export default function NeuralCVLab({ seedImageSrc = null }) {
  const [tab, setTab] = useState('convolution');
  const [inputVals, setInputVals] = useState([[0,0,0,0,0],[0,0,5,0,0],[0,5,10,5,0],[0,0,5,0,0],[0,0,0,0,0]]);
  const [kernel, setKernel] = useState([[0,-1,0],[-1,5,-1],[0,-1,0]]);
  const [activePreset, setActivePreset] = useState('sharpen');
  const [imgSrc, setImgSrc] = useState(null);
  const [imgData, setImgData] = useState(null);
  const [convImgSrc, setConvImgSrc] = useState(null);
  const [activeKernel, setActiveKernel] = useState('sharpen');

  const convOutput = useMemo(() => computeConv(inputVals, kernel), [inputVals, kernel]);

  const loadSynth = async (type) => {
    const src = generateSynth(type, 256);
    setImgSrc(src);
    const id = await imgToImageData(src);
    setImgData(id);
    const out = convolve(id, KERNELS[activeKernel] || KERNELS.sharpen, 1, 0);
    setConvImgSrc(imageDataToURL(out));
  };

  const applyKernelToImg = () => {
    if (!imgData) return;
    const kMap = {identity:KERNELS.identity,sharpen:KERNELS.sharpen,blur:KERNELS.blur,sobelX:KERNELS.sobelX,laplacian:KERNELS.laplacian};
    const out = convolve(imgData, kMap[activeKernel]||KERNELS.sharpen, activeKernel==='blur'?9:1, activeKernel==='laplacian'?128:0);
    setConvImgSrc(imageDataToURL(out));
  };

  useEffect(() => {
    if (!seedImageSrc) return;
    (async () => {
      setImgSrc(seedImageSrc);
      const id = await imgToImageData(seedImageSrc);
      setImgData(id);
      const out = convolve(id, KERNELS[activeKernel] || KERNELS.sharpen, activeKernel === 'blur' ? 9 : 1, activeKernel === 'laplacian' ? 128 : 0);
      setConvImgSrc(imageDataToURL(out));
    })();
  }, [seedImageSrc, activeKernel]);

  const actData = useMemo(() => {
    const xs = Array.from({length:41},(_,i)=>-2+i*0.1);
    return xs.map(x=>({x:+x.toFixed(1),...Object.fromEntries(ACTIVATIONS.map(a=>[a.id,+a.fn(x).toFixed(4)]))}));
  },[]);

  return (
    <div className="anim-fadeup" style={{display:'flex',flexDirection:'column',gap:'12px'}}>
      <div style={{display:'flex',gap:'3px',background:'var(--deep)',padding:'4px',borderRadius:'3px',border:'1px solid var(--b0)',overflowX:'auto'}}>
        {TABS.map(t=><button key={t} onClick={()=>setTab(t)} style={{padding:'6px 14px',border:`1px solid ${tab===t?'#f472b6':'transparent'}`,background:tab===t?'#f472b618':'transparent',color:tab===t?'#f472b6':'var(--t3)',fontSize:'10px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono',whiteSpace:'nowrap',fontWeight:tab===t?700:400}}>{t.toUpperCase()}</button>)}
      </div>

      {/* INTERACTIVE CONVOLUTION */}
      {tab==='convolution' && (
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          <Panel title="INTERACTIVE CONVOLUTION DEMO" accent="#f472b6">
            <div style={{fontSize:'11px',color:'var(--t3)',lineHeight:1.7,marginBottom:'14px'}}>
              Edit the 5×5 input matrix and 3×3 kernel below. The 3×3 output is computed live. Click a kernel preset to load it.
            </div>
            <div style={{display:'flex',gap:'8px',marginBottom:'14px',flexWrap:'wrap'}}>
              {Object.entries(PRESET_KERNELS).map(([name,k])=>(
                <button key={name} onClick={()=>{setActivePreset(name);setKernel(k);}} style={{padding:'5px 12px',border:`1px solid ${activePreset===name?'#f472b6':'var(--b0)'}`,background:activePreset===name?'#f472b618':'var(--raised)',color:activePreset===name?'#f472b6':'var(--t2)',fontSize:'10px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono',fontWeight:activePreset===name?700:400}}>{name}</button>
              ))}
            </div>
            <div style={{display:'grid',gridTemplateColumns:'auto auto auto',gap:'32px',alignItems:'start'}}>
              <div>
                <div style={{fontSize:'9px',color:'var(--t3)',marginBottom:'8px',letterSpacing:'.1em'}}>5×5 INPUT</div>
                {inputVals.map((row,y)=>(
                  <div key={y} style={{display:'flex',gap:'3px',marginBottom:'3px'}}>
                    {row.map((v,x)=>(
                      <input key={x} type="number" value={v} onChange={e=>{const n=[...inputVals.map(r=>[...r])];n[y][x]=+e.target.value;setInputVals(n);}} style={{width:'42px',textAlign:'center',background:'var(--deep)',border:'1px solid var(--b1)',color:'var(--cyan)',padding:'5px',fontSize:'11px',fontFamily:'JetBrains Mono',borderRadius:'2px',outline:'none'}}/>
                    ))}
                  </div>
                ))}
              </div>
              <div>
                <div style={{fontSize:'9px',color:'var(--t3)',marginBottom:'8px',letterSpacing:'.1em'}}>3×3 KERNEL</div>
                {kernel.map((row,y)=>(
                  <div key={y} style={{display:'flex',gap:'3px',marginBottom:'3px'}}>
                    {row.map((v,x)=>(
                      <input key={x} type="number" value={v} onChange={e=>{const n=kernel.map(r=>[...r]);n[y][x]=+e.target.value;setKernel(n);}} style={{width:'42px',textAlign:'center',background:'var(--deep)',border:'1px solid #f472b6',color:'#f472b6',padding:'5px',fontSize:'11px',fontFamily:'JetBrains Mono',borderRadius:'2px',outline:'none'}}/>
                    ))}
                  </div>
                ))}
              </div>
              <div>
                <div style={{fontSize:'9px',color:'var(--t3)',marginBottom:'8px',letterSpacing:'.1em'}}>3×3 OUTPUT</div>
                {convOutput.map((row,y)=>(
                  <div key={y} style={{display:'flex',gap:'3px',marginBottom:'3px'}}>
                    {row.map((v,x)=>(
                      <div key={x} style={{width:'42px',height:'31px',background:`rgba(244,114,182,${Math.min(1,Math.abs(v)/20)})`,border:'1px solid #f472b644',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'9px',fontFamily:'JetBrains Mono',color:'var(--t1)',borderRadius:'2px'}}>{v}</div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </Panel>
          <Panel title="APPLY KERNEL TO IMAGE" accent="#f472b6">
            <div style={{display:'flex',gap:'8px',flexWrap:'wrap',marginBottom:'12px'}}>
              {Object.keys(PRESET_KERNELS).map(name=>(
                <button key={name} onClick={()=>setActiveKernel(name)} style={{padding:'5px 12px',border:`1px solid ${activeKernel===name?'#f472b6':'var(--b0)'}`,background:activeKernel===name?'#f472b618':'var(--raised)',color:activeKernel===name?'#f472b6':'var(--t2)',fontSize:'10px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono'}}>{name}</button>
              ))}
            </div>
            <div style={{display:'flex',gap:'8px',flexWrap:'wrap',marginBottom:'12px'}}>
              {[{name:'Shapes',type:'shapes'},{name:'Circles',type:'circles'},{name:'Checker',type:'checker'}].map(({name,type})=>(
                <button key={type} onClick={()=>loadSynth(type)} style={{padding:'5px 12px',border:'1px solid var(--b0)',background:'var(--raised)',color:'var(--t2)',fontSize:'10px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono'}}>Load {name}</button>
              ))}
              {imgSrc && <Btn onClick={applyKernelToImg}>APPLY KERNEL →</Btn>}
            </div>
            {imgSrc && convImgSrc && (
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px'}}>
                <div><div style={{fontSize:'10px',color:'var(--t3)',marginBottom:'5px'}}>ORIGINAL</div><img src={imgSrc} alt="orig" style={{width:'100%',borderRadius:'3px',border:'1px solid var(--b1)'}}/></div>
                <div><div style={{fontSize:'10px',color:'#f472b6',fontWeight:700,marginBottom:'5px'}}>KERNEL: {activeKernel.toUpperCase()}</div><img src={convImgSrc} alt="result" style={{width:'100%',borderRadius:'3px',border:'1px solid #f472b644'}}/></div>
              </div>
            )}
          </Panel>
        </div>
      )}

      {/* ACTIVATIONS */}
      {tab==='activations' && (
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          <Panel title="ACTIVATION FUNCTIONS" accent="var(--cyan)">
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={actData}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                <XAxis dataKey="x" stroke="var(--t3)" tick={{fontSize:9}} label={{value:'x input',position:'insideBottom',offset:-2,fill:'var(--t3)',fontSize:9}}/>
                <YAxis stroke="var(--t3)" tick={{fontSize:9}} domain={[-1.5,2]}/>
                <Tooltip/>
                <Legend/>
                {ACTIVATIONS.map(a=><Line key={a.id} type="monotone" dataKey={a.id} stroke={a.color} strokeWidth={2} dot={false} name={a.label}/>)}
              </LineChart>
            </ResponsiveContainer>
          </Panel>
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))',gap:'10px'}}>
            {ACTIVATIONS.map(a=>(
              <div key={a.id} style={{padding:'14px',background:'var(--raised)',border:`1px solid ${a.color}33`,borderRadius:'4px'}}>
                <div style={{color:a.color,fontWeight:700,fontSize:'12px',marginBottom:'4px'}}>{a.label}</div>
                <div style={{fontFamily:'JetBrains Mono',fontSize:'11px',color:'var(--cyan)',marginBottom:'6px'}}>{a.formula}</div>
                <div style={{fontSize:'10px',color:'var(--t3)',marginBottom:'4px'}}>Range: <span style={{color:'var(--t2)'}}>{a.range}</span></div>
                <div style={{fontSize:'10px',color:'var(--t3)',lineHeight:1.6}}>{a.use}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* POOLING */}
      {tab==='pooling' && (
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          <Panel title="POOLING LAYERS — MAX vs AVERAGE" accent="var(--gold)">
            <div style={{fontSize:'11px',color:'var(--t3)',lineHeight:1.7,marginBottom:'14px'}}>
              Pooling reduces spatial resolution (downsamples) while preserving the most important features. A 2×2 max pool on a 4×4 → 2×2 output, halving width and height, quartering parameter count.
            </div>
            {(() => {
              const input4=[[1,2,3,4],[5,6,7,8],[9,10,11,12],[13,14,15,16]];
              const maxPool=[[6,8],[14,16]],avgPool=[[3.5,5.5],[11.5,13.5]];
              const Cell2=({v,color,style})=><div style={{width:'50px',height:'50px',display:'flex',alignItems:'center',justifyContent:'center',fontFamily:'JetBrains Mono',fontSize:'13px',fontWeight:700,color:color||'var(--t1)',background:'var(--raised)',border:'1px solid var(--b1)',borderRadius:'2px',...style}}>{v}</div>;
              return (
                <div style={{display:'grid',gridTemplateColumns:'auto auto auto',gap:'40px',alignItems:'center'}}>
                  <div>
                    <div style={{fontSize:'9px',color:'var(--t3)',marginBottom:'8px',letterSpacing:'.1em'}}>4×4 INPUT</div>
                    {input4.map((row,y)=><div key={y} style={{display:'flex',gap:'2px',marginBottom:'2px'}}>{row.map((v,x)=><Cell2 key={x} v={v} color={(y<2&&x<2)||(y>=2&&x>=2)||true?'var(--t2)':undefined}/>)}</div>)}
                  </div>
                  <div style={{display:'flex',flexDirection:'column',gap:'20px'}}>
                    <div>
                      <div style={{fontSize:'9px',color:'var(--green)',marginBottom:'8px',fontWeight:700}}>2×2 MAX POOL</div>
                      {maxPool.map((row,y)=><div key={y} style={{display:'flex',gap:'2px',marginBottom:'2px'}}>{row.map((v,x)=><Cell2 key={x} v={v} color="var(--green)" style={{border:'2px solid var(--green)'}}/>)}</div>)}
                    </div>
                    <div>
                      <div style={{fontSize:'9px',color:'var(--orange)',marginBottom:'8px',fontWeight:700}}>2×2 AVG POOL</div>
                      {avgPool.map((row,y)=><div key={y} style={{display:'flex',gap:'2px',marginBottom:'2px'}}>{row.map((v,x)=><Cell2 key={x} v={v} color="var(--orange)" style={{border:'2px solid var(--orange)'}}/>)}</div>)}
                    </div>
                  </div>
                  <Panel title="KEY DIFFERENCES" accent="var(--gold)">
                    <div style={{fontSize:'11px',color:'var(--t3)',lineHeight:1.8}}>
                      <strong style={{color:'var(--green)'}}>Max Pooling</strong> — keeps strongest activation. Good for detecting features anywhere in the region. Standard in classification networks.<br/><br/>
                      <strong style={{color:'var(--orange)'}}>Average Pooling</strong> — smooth aggregation. Global Average Pooling (GAP) reduces entire spatial map to 1×1 before classifier — eliminates large FC layers.<br/><br/>
                      <strong style={{color:'var(--violet)'}}>Stride 2 Conv</strong> — learned downsampling. Modern networks (ResNet, EfficientNet) often prefer this over fixed pooling.
                    </div>
                  </Panel>
                </div>
              );
            })()}
          </Panel>
        </div>
      )}

      {/* ARCHITECTURES */}
      {tab==='architectures' && (
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(300px,1fr))',gap:'12px'}}>
            {CNN_ARCHS.map(a=>(
              <div key={a.name} style={{padding:'16px',background:'var(--raised)',border:`1px solid ${a.color}33`,borderRadius:'4px'}}>
                <div style={{display:'flex',justifyContent:'space-between',marginBottom:'8px'}}>
                  <span style={{color:a.color,fontWeight:700,fontSize:'13px'}}>{a.name}</span>
                  <span style={{fontSize:'10px',color:'var(--t3)',fontFamily:'JetBrains Mono'}}>{a.params} params</span>
                </div>
                <div style={{fontSize:'11px',color:'var(--t3)',lineHeight:1.7}}>{a.desc}</div>
              </div>
            ))}
          </div>
          <Panel title="TYPICAL CNN PIPELINE" accent="#f472b6">
            <div style={{display:'flex',gap:'6px',overflowX:'auto',padding:'4px 0'}}>
              {[{l:'INPUT',s:'224×224×3',c:'var(--t2)'},{l:'CONV3',s:'224×224×64',c:'var(--cyan)'},{l:'POOL',s:'112×112×64',c:'var(--gold)'},{l:'CONV3',s:'112×112×128',c:'var(--cyan)'},{l:'BN',s:'batch norm',c:'var(--violet)'},{l:'POOL',s:'56×56×128',c:'var(--gold)'},{l:'CONV3',s:'56×56×256',c:'var(--cyan)'},{l:'GAP',s:'1×1×256',c:'var(--orange)'},{l:'FC',s:'1×256',c:'#f472b6'},{l:'FC',s:'1×1000',c:'#f472b6'},{l:'SOFTMAX',s:'class probs',c:'var(--green)'}].map(({l,s,c},i)=>(
                <div key={i} style={{display:'flex',flexDirection:'column',alignItems:'center',gap:'4px',flexShrink:0}}>
                  <div style={{padding:'8px 10px',background:c+'20',border:`1px solid ${c}66`,borderRadius:'3px',textAlign:'center',minWidth:'64px'}}>
                    <div style={{fontSize:'10px',fontWeight:700,color:c,fontFamily:'JetBrains Mono'}}>{l}</div>
                    <div style={{fontSize:'8px',color:'var(--t3)',marginTop:'2px'}}>{s}</div>
                  </div>
                  {i<10&&<div style={{color:'var(--t3)',fontSize:'10px'}}>→</div>}
                </div>
              ))}
            </div>
          </Panel>
        </div>
      )}

      {/* RECEPTIVE FIELD */}
      {tab==='receptive' && (
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          <Panel title="RECEPTIVE FIELD GROWTH" accent="var(--violet)">
            <div style={{fontSize:'11px',color:'var(--t3)',lineHeight:1.7,marginBottom:'14px'}}>
              Each conv layer with 3×3 kernel expands the receptive field by 2. After L layers: RF = 2L+1. With stride 2: RF grows exponentially. A deep network "sees" a large region of the original image for each output neuron.
            </div>
            <div style={{display:'flex',gap:'12px',overflowX:'auto',padding:'4px 0',alignItems:'flex-end'}}>
              {[{layer:'Input',rf:1},{layer:'Conv1',rf:3},{layer:'Conv2',rf:5},{layer:'Conv3',rf:7},{layer:'Conv4 (stride 2)',rf:15},{layer:'Conv5 (stride 2)',rf:31},{layer:'Conv6 (stride 4)',rf:63}].map(({layer,rf},i)=>(
                <div key={i} style={{display:'flex',flexDirection:'column',alignItems:'center',gap:'6px',flexShrink:0}}>
                  <div style={{fontSize:'9px',color:'var(--violet)',fontWeight:700}}>{rf}px RF</div>
                  <div style={{width:'60px',height:`${Math.min(160,rf*2.5)}px`,background:'var(--violet)',opacity:.15+i*.1,borderRadius:'3px',border:'1px solid var(--violet)44'}}/>
                  <div style={{fontSize:'8px',color:'var(--t3)',textAlign:'center',maxWidth:'60px'}}>{layer}</div>
                </div>
              ))}
            </div>
          </Panel>
          <Panel title="TRANSFER LEARNING PIPELINE" accent="var(--cyan)">
            <div style={{display:'grid',gridTemplateColumns:'repeat(5,1fr)',gap:'10px'}}>
              {[['1. Pre-trained Backbone','ResNet/EfficientNet trained on ImageNet (1.2M images, 1000 classes)','var(--green)'],['2. Freeze Layers','Lock weights of early conv layers — they already detect edges/textures universally','var(--gold)'],['3. Add Head','Replace final FC layer with new classifier for your task (e.g., 2 classes for cat/dog)','var(--cyan)'],['4. Fine-tune','Unfreeze last N layers, train with small lr (1e-4) on your domain-specific data','var(--violet)'],['5. Domain Adapt','For very different domains, use domain adaptation or few-shot techniques','var(--orange)']].map(([step,desc,color])=>(
                <div key={step} style={{padding:'12px',background:'var(--raised)',borderLeft:`3px solid ${color}`,borderRadius:'2px'}}>
                  <div style={{fontSize:'10px',fontWeight:700,color,marginBottom:'5px'}}>{step}</div>
                  <div style={{fontSize:'10px',color:'var(--t3)',lineHeight:1.6}}>{desc}</div>
                </div>
              ))}
            </div>
          </Panel>
        </div>
      )}

      {/* CONCEPTS */}
      {tab==='concepts' && (
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px'}}>
          {[
            {title:'Why Convolutions?',color:'#f472b6',desc:'Parameter sharing: one kernel scans the whole image — only 9 weights for a 3×3 conv vs. millions for a dense layer. Translation equivariance: same kernel detects a feature regardless of its position in the image.'},
            {title:'Batch Normalization',color:'var(--cyan)',desc:'Normalizes activations to zero mean/unit variance per mini-batch. Reduces internal covariate shift. Allows higher learning rates. Acts as regularization. Applied before or after activation. Almost always used in modern nets.'},
            {title:'Skip Connections (ResNet)',color:'var(--green)',desc:'Add input directly to output: y = F(x) + x. Gradient flows directly to early layers — no vanishing gradient. Also allows learning identity mappings (do nothing). Enables training of 1000+ layer networks.'},
            {title:'Attention in Vision',color:'var(--gold)',desc:'Self-attention (ViT) computes pairwise relationships between all image patches. Captures long-range dependencies that conv layers miss. Scales with O(n²) — expensive for large images. SWIN Transformer uses local attention windows.'},
            {title:'Foundation Models',color:'var(--orange)',desc:'CLIP: learns image-text alignment from 400M pairs. SAM: segment anything with a click — trained on 1B+ masks. DINO: self-supervised visual features. These are pre-trained on enormous data and fine-tuned for downstream tasks.'},
            {title:'Data Augmentation',color:'var(--violet)',desc:'Random crops, flips, rotations, color jitter, cutout, mixup, cutmix artificially multiply training data. Crucial for small datasets. Advanced: AutoAugment learns optimal augmentation policies. RandAugment is simpler but competitive.'},
          ].map(({title,color,desc})=>(
            <div key={title} style={{padding:'16px',background:'var(--raised)',border:`1px solid ${color}33`,borderRadius:'4px'}}>
              <div style={{color,fontWeight:700,fontSize:'13px',marginBottom:'8px'}}>{title}</div>
              <div style={{fontSize:'11px',color:'var(--t3)',lineHeight:1.7}}>{desc}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
